import { useEffect, useCallback, useMemo, useState, type ReactNode, type CSSProperties } from 'react'
import { Search, X } from 'lucide-react'
import { SidebarSection } from './SidebarSection'
import { SidebarGroup } from './SidebarGroup'
import { SidebarItem } from './SidebarItem'
import { applyVisibility, applySearch } from './utils'
import type { SidebarEntry, SidebarItemDef, SidebarContext } from './types'
import styles from './Sidebar.module.css'

export interface SidebarProps {
  items: SidebarEntry[]
  /** Current user context used to evaluate item/section/group visibility rules. */
  context?: SidebarContext
  isOpen: boolean
  onClose: () => void
  header?: ReactNode
  footer?: ReactNode
  className?: string
  style?: CSSProperties
  collapsed?: boolean
  /**
   * When true, the sidebar behaves as an off-canvas drawer at ALL widths (not just
   * mobile) — hidden by default, slides in with a backdrop when `isOpen`. Useful for
   * marketing/landing layouts that want navigation on demand without a fixed sidebar.
   */
  floating?: boolean
  /** When true, renders a search input below the header that filters items client-side. */
  searchable?: boolean
  /** Placeholder text for the search input. */
  searchPlaceholder?: string
  /**
   * Scopes localStorage keys for group collapse state, preventing collisions between apps
   * (e.g. internal admin vs org admin). Final key format: `nexus-sidebar-group:{prefix}:{groupId}`.
   */
  storageKeyPrefix?: string
  /** Called when any item is clicked. Useful for closing the mobile drawer + analytics. */
  onItemClick?: (item: SidebarItemDef) => void
  /**
   * `default` keeps the gradient hover sweep and glowing active icon.
   * `quiet` is near-monochrome: a neutral fill on hover, and on the active
   * item a neutral fill plus a 2px accent bar on its left edge. For a nav of a
   * few places where the page, not the chrome, should carry the colour.
   */
  variant?: 'default' | 'quiet'
}

export function Sidebar({
  items,
  context,
  isOpen,
  onClose,
  header,
  footer,
  className,
  style,
  collapsed,
  floating,
  searchable,
  searchPlaceholder = '',
  storageKeyPrefix,
  onItemClick,
  variant = 'default',
}: SidebarProps & { children?: never }) {
  const [query, setQuery] = useState('')

  const handleEscape = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') onClose()
  }, [onClose])

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
      return () => document.removeEventListener('keydown', handleEscape)
    }
  }, [isOpen, handleEscape])

  // Clear search when drawer closes or sidebar collapses so reopening starts fresh
  useEffect(() => {
    if (!isOpen || collapsed) setQuery('')
  }, [isOpen, collapsed])

  const filtered = useMemo(() => {
    const visible = applyVisibility(items, context)
    return applySearch(visible, query)
  }, [items, context, query])

  const searching = query.trim().length > 0

  return (
    <>
      {isOpen && <div className={[styles.backdrop, floating && styles.backdropFloating].filter(Boolean).join(' ')} onClick={onClose} />}
      <aside
        className={[styles.sidebar, isOpen && styles.open, collapsed && styles.collapsed, floating && styles.floating, variant === 'quiet' && styles.quiet, className].filter(Boolean).join(' ')}
        style={style}
        role="navigation"
        aria-label="Sidebar navigation"
      >
        {header && <div className={styles.header}>{header}</div>}

        {searchable && !collapsed && (
          <div className={styles.searchWrap}>
            <Search size={14} className={styles.searchIcon} aria-hidden="true" />
            <input
              type="text"
              className={styles.searchInput}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={searchPlaceholder}
              aria-label={searchPlaceholder}
              autoComplete="off"
              spellCheck={false}
            />
            {query && (
              <button
                type="button"
                className={styles.searchClear}
                onClick={() => setQuery('')}
                aria-label="Clear search"
              >
                <X size={14} />
              </button>
            )}
          </div>
        )}

        <nav className={styles.nav}>
          {filtered.length === 0 && searching && (
            <div className={styles.emptyState}>No matches for &ldquo;{query}&rdquo;</div>
          )}
          {filtered.map((entry) => {
            if (entry.kind === 'section') {
              return (
                <SidebarSection key={entry.id} label={entry.label ?? ''}>
                  {entry.items.map((item) => (
                    <SidebarItem key={item.id} def={item} onItemClick={onItemClick} collapsed={collapsed} storageKey={storageKeyPrefix ? `${storageKeyPrefix}:${entry.id}:${item.id}` : `${entry.id}:${item.id}`} forceExpanded={searching} />
                  ))}
                  {entry.groups?.map((group) => (
                    <SidebarGroup
                      key={group.id}
                      label={group.label}
                      defaultOpen={group.defaultOpen ?? true}
                      storageKey={storageKeyPrefix ? `${storageKeyPrefix}:${entry.id}:${group.id}` : `${entry.id}:${group.id}`}
                      forceOpen={searching}
                      nested
                    >
                      {group.items.map((item) => (
                        <SidebarItem key={item.id} def={item} onItemClick={onItemClick} collapsed={collapsed} storageKey={storageKeyPrefix ? `${storageKeyPrefix}:${entry.id}:${group.id}:${item.id}` : `${entry.id}:${group.id}:${item.id}`} forceExpanded={searching} />
                      ))}
                    </SidebarGroup>
                  ))}
                </SidebarSection>
              )
            }
            const groupStorageKey = storageKeyPrefix
              ? `${storageKeyPrefix}:${entry.id}`
              : entry.id
            return (
              <SidebarGroup
                key={entry.id}
                label={entry.label}
                defaultOpen={entry.defaultOpen ?? true}
                storageKey={groupStorageKey}
                forceOpen={searching}
              >
                {entry.items.map((item) => (
                  <SidebarItem key={item.id} def={item} onItemClick={onItemClick} collapsed={collapsed} storageKey={storageKeyPrefix ? `${storageKeyPrefix}:${entry.id}:${item.id}` : `${entry.id}:${item.id}`} forceExpanded={searching} />
                ))}
              </SidebarGroup>
            )
          })}
        </nav>

        {footer && <div className={styles.footer}>{footer}</div>}
      </aside>
    </>
  )
}
